import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export function SwapDialog({ sale, onClose, onSwapped }) {
  const [products, setProducts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [returnedId, setReturnedId] = useState("");
  const [newPid, setNewPid] = useState("");
  const [newName, setNewName] = useState("");
  const [newSearch, setNewSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [unitPrice, setUnitPrice] = useState("");
  const [diff, setDiff] = useState("");
  const [bankId, setBankId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!sale) return;
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    api.get("/banks").then((r) => setBanks(r.data || [])).catch(() => {});
    setReturnedId(""); setNewPid(""); setNewName(""); setNewSearch("");
    setUnitPrice(""); setDiff(""); setBankId(""); setReason("");
  }, [sale]);

  if (!sale) return null;

  const saleItems = sale.items || [];
  // For non-super-admin users `/banks` returns only id+name (no currency).
  // If currency is present, filter by sale.currency; otherwise show all banks.
  const banksHaveCurrency = banks.some((b) => b.currency);
  const banksForSwap = banksHaveCurrency ? banks.filter((b) => b.currency === sale.currency) : banks;
  const newMatches = newSearch.trim().length >= 2
    ? products.filter((p) => `${p.name} ${p.brand || ""} ${p.sku || ""}`.toLowerCase().includes(newSearch.toLowerCase())).slice(0, 8)
    : [];

  const submit = async () => {
    if (!reason.trim()) return toast.error("Indica el motivo del cambio");
    if (!newPid && !newName.trim()) return toast.error("Indica el equipo nuevo");
    const dnum = parseFloat(diff) || 0;
    if (dnum > 0 && !bankId) return toast.error("Selecciona el banco donde se recibió la diferencia");
    setSaving(true);
    try {
      const body = {
        returned_product_id: returnedId || null,
        returned_qty: 1,
        new_product_id: newPid || null,
        new_product_name: newName.trim() || null,
        new_unit_price: parseFloat(unitPrice) || 0,
        diferencia: dnum,
        bank_id: bankId || null,
        reason: reason.trim(),
      };
      const { data } = await api.post(`/sales/${sale.id}/swap`, body);
      toast.success("Cambio registrado");
      onSwapped(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrar el cambio");
    } finally { setSaving(false); }
  };

  const fmtCur = (n) => `${sale.currency === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

  return (
    <Dialog open={!!sale} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Cambio de equipo / Garantía</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded p-3">
            Nota #{String(sale.folio).padStart(6, "0")} · Total actual <b>{fmtCur(sale.total)}</b>
          </div>

          {/* Returned item from the sale */}
          <div>
            <Label className="text-xs uppercase tracking-wider">Equipo devuelto (de esta nota)</Label>
            <select
              value={returnedId}
              onChange={(e) => setReturnedId(e.target.value)}
              className="mt-1 w-full border border-zinc-300 rounded px-2 py-2 text-sm bg-white"
              data-testid="swap-returned"
            >
              <option value="">— No retorna nada al inventario —</option>
              {saleItems.map((it) => (
                <option key={it.id} value={it.product_id || ""} disabled={!it.product_id}>
                  {it.product_name} ({it.qty} × {fmtCur(it.unit_price)}){!it.product_id ? " · sin inventario" : ""}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-500 mt-1">Si el equipo estaba en inventario, regresa a stock en la categoría <b>&quot;Garantía&quot;</b> para no mezclarlo con productos nuevos.</p>
          </div>

          {/* New product (inventory search OR free text) */}
          <div>
            <Label className="text-xs uppercase tracking-wider">Equipo nuevo</Label>
            <div className="relative mt-1">
              <Input
                value={newPid ? products.find((p) => p.id === newPid)?.name || "" : newSearch}
                onChange={(e) => { setNewPid(""); setNewSearch(e.target.value); setNewOpen(true); }}
                onFocus={() => setNewOpen(true)}
                placeholder="Busca en inventario o escribe libremente…"
                data-testid="swap-new-search"
              />
              {newOpen && newMatches.length > 0 && (
                <div className="absolute z-40 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded shadow-lg max-h-64 overflow-y-auto">
                  {newMatches.map((p) => (
                    <button type="button" key={p.id}
                      onClick={() => { setNewPid(p.id); setNewName(""); setNewSearch(p.name); setNewOpen(false); setUnitPrice(String(p.marketplace_price || p.store_price || 0)); }}
                      className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 text-sm"
                      data-testid={`swap-new-opt-${p.id}`}>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-[11px] text-zinc-500">{[p.brand, p.category].filter(Boolean).join(" · ")} · stock {p.stock}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!newPid && newSearch && (
              <button type="button" onClick={() => { setNewName(newSearch); setNewOpen(false); }}
                className="mt-2 text-xs text-[#002FA7] underline" data-testid="swap-use-freetext">
                Usar &quot;{newSearch}&quot; como equipo libre (sin afectar inventario)
              </button>
            )}
            {newName && !newPid && <p className="text-[11px] text-amber-700 mt-1">Texto libre: <b>{newName}</b> (no afecta inventario)</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Precio unitario nuevo</Label>
              <Input type="number" step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="mt-1" data-testid="swap-unit-price" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Diferencia que pagó el cliente</Label>
              <Input type="number" step="0.01" value={diff} onChange={(e) => setDiff(e.target.value)} placeholder="0.00 si no pagó nada" className="mt-1" data-testid="swap-diff" />
            </div>
          </div>

          {parseFloat(diff || 0) > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-wider">Banco donde se recibió *</Label>
              <select value={bankId} onChange={(e) => setBankId(e.target.value)} className="mt-1 w-full border border-zinc-300 rounded px-2 py-2 text-sm bg-white" data-testid="swap-bank">
                <option value="">{banksForSwap.length ? "Selecciona…" : `Sin bancos${banksHaveCurrency ? ` en ${sale.currency}` : ""}`}</option>
                {banksForSwap.map((b) => <option key={b.id} value={b.id}>{b.name}{b.currency ? ` (${b.currency})` : ""}</option>)}
              </select>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider">Motivo del cambio *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Ej. Cliente solicitó upgrade / equipo presentaba falla en pantalla" className="mt-1" data-testid="swap-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#7E57C2] hover:bg-[#6a47ad] text-white" data-testid="swap-submit">
            {saving ? "Guardando…" : "Registrar cambio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
